import { FC, useState, useRef, useEffect } from 'react';
import { Loader2, Send } from 'lucide-react';
import { cn } from '@new/lib';
import { Button } from '@new/common/ui/Button/Button';
import { useChatSocket } from '@new/features/chat/hooks/useChatSocket';
import { useVideoCallContext } from '../../../../../contexts/VideoCallProvider';
import { useVideoCallChat } from '../../hooks/useVideoCallChat';
import type { Message } from '@teachur/http-client';

export const ChatPanelContainer: FC = () => {
  const { videoCall, user } = useVideoCallContext();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const chat = useVideoCallChat({
    currentUserId: user?.id,
    studentId: videoCall?.student?.id,
    instructorId: videoCall?.instructor?.id,
  });

  useChatSocket(chat.chatId);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat.chat?.messages]);

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    chat.sendMessage(trimmed);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (chat.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (!chat.chat) {
    return (
      <div className="flex items-center justify-center h-64 p-4">
        <p className="text-neutral-500 text-sm">No chat available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-4">
      <h3 className="font-bold [font-size:var(--fs-h6)]! text-(--neutral-black-NB800)! m-0!">
        Chat
      </h3>

      <div className="flex-1 overflow-y-auto px-4 py-2 flex flex-col gap-3">
        {chat.chat.messages
          .sort(
            (a: Message, b: Message) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          )
          .map((message: Message) => {
            const isMine = message.userId === chat.currentUserId;
            const messageUser = chat.chat?.users.find((user) => user.id === message.userId);
            return (
              <div
                key={message.id}
                className={cn('flex', isMine ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    'max-w-[75%] flex flex-col gap-1 rounded-lg px-3 py-2 m-0!',
                    isMine
                      ? 'bg-(--secondary-B900) text-(--neutral-white-NW1000)'
                      : 'bg-neutral-200 text-neutral-800'
                  )}
                >
                  <p className="[font-size:var(--fs-l4)] font-semibold m-0! opacity-80">
                    {isMine
                      ? 'You'
                      : `${messageUser?.firstName || ''} ${messageUser?.lastName || ''}`.trim() ||
                        messageUser?.email ||
                        'Unknown'}
                  </p>
                  <p className="[font-size:var(--fs-l3)] m-0!">{message.text}</p>
                  <p className="[font-size:var(--fs-l4)] opacity-60 m-0!">
                    {new Date(message.createdAt).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            );
          })}
        <div ref={messagesEndRef} />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your message here..."
          disabled={chat.isSending}
          className="flex-1 bg-(--neutral-white-NW1000) rounded-lg! px-4 py-2.5 [font-size:var(--fs-l3)] outline-none placeholder:text-(--neutral-white-NW600)"
        />
        <Button
          onClick={handleSend}
          disabled={!inputValue.trim() || chat.isSending}
          variant="primary"
          size="sm"
          className="w-10 h-10 p-0 rounded-full"
          aria-label="Send message"
        >
          <Send size={16} />
        </Button>
      </div>
    </div>
  );
};
